<template>
  <div class="shape-style-panel">
    <div class="title">
      <span>Click to replace shape</span>
    </div>
    <div class="shape-pool">
      <div class="category" v-for="item in SHAPE_LIST" :key="item.type">
        <div class="shape-list">
          <ShapeItemThumbnail
            class="shape-item"
            v-for="(shape, index) in item.children"
            :key="index"
            :shape="shape"
            @click="changeShape(shape)"
          />
        </div>
      </div>
    </div>

    <div class="group">
      <Dropdown v-model="fillType"
                style="margin-right:5px;"
                :options="[
                    {label: 'Solid Color Fill', value: 'fill'},
                    {label: 'Gradient Fill', value: 'gradient'}]"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Fill Type"
                @change="updateFillType(fillType as 'fill' | 'gradient' )" />

        <ColorButton v-if="fillType === 'fill'" @click="$refs.colorPickerVisible.toggle($event)" :color="fill" style="flex: 3" />
        <OverlayPanel v-if="fillType === 'fill'" ref="colorPickerVisible">
          <ColorPicker
              :modelValue="fill"
              @update:modelValue="value => updateFill(value)"
          />
        </OverlayPanel>

      <Dropdown v-model="gradient.type"
                style="flex: 3;"
                :options="[
                    {label: 'Linear Gradient', value: 'linear'},
                    {label: 'Radial Gradient', value: 'radial'}]"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Fill Type"
                @change="updateGradient({ type: gradient.type as 'linear' | 'radial' })"
                v-else />
    </div>

    <template v-if="fillType === 'gradient'">
      <div class="group">
        Starting color：
        <ColorButton @click="$refs.colorPickerVisible1.toggle($event)" :color="gradient.color[0]" style="flex: 3"  />
        <OverlayPanel ref="colorPickerVisible1">
          <ColorPicker
              :modelValue="gradient.color[0]"
              @update:modelValue="value => updateGradient({ color: [value, gradient.color[1]] })"
          />
        </OverlayPanel>
      </div>
      <div class="group" style="padding-bottom:10px;">
        End color:
        <ColorButton @click="$refs.colorPickerVisible2.toggle($event)" :color="gradient.color[1]" style="flex: 3" />
        <OverlayPanel ref="colorPickerVisible2">
          <ColorPicker
              :modelValue="gradient.color[1]"
              @update:modelValue="value => updateGradient({ color: [gradient.color[0], value] })"
          />
        </OverlayPanel>
      </div>

      <div class="group">
      <ElementFlip style="flex: 1;"/>
      </div>

      <div class="group">
        Opacity ({{ handleElement.opacity ? handleElement.opacity : 1 }}):
        <ElementOpacity style="flex: 1;"/>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore, useSlidesStore } from '@/store'
import { PPTShapeElement, ShapeGradient } from '@/types/slides'
import { ShapePoolItem, SHAPE_LIST, SHAPE_PATH_FORMULAS } from '@/configs/shapes'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'

import ColorButton from '../common/ColorButton.vue'
import ColorPicker from '@/components/ColorPicker/index.vue'
import ShapeItemThumbnail from '@/views/Editor/CanvasTool/ShapeItemThumbnail.vue'
import ElementFlip from '../common/ElementFlip.vue'
import ElementOpacity from '../common/ElementOpacity.vue'

import useMicroservice from "@/hooks/useMicroservice.ts";
const api = useMicroservice()
const mainStore = useMainStore()
const slidesStore = useSlidesStore()
const { handleElement, handleElementId } = storeToRefs(mainStore)

const fill = ref<string>('#000')
const gradient = ref<ShapeGradient>({
  type: 'linear',
  rotate: 0,
  color: ['#fff', '#fff'],
})
const fillType = ref('fill')
const textAlign = ref('middle')

watch(handleElement, async () => {
  if (!handleElement.value || handleElement.value.type !== 'shape') return

  fill.value = handleElement.value.fill || '#fff'
  gradient.value = handleElement.value.gradient || { type: 'linear', rotate: 0, color: [fill.value, '#fff'] }
  fillType.value = handleElement.value.gradient ? 'gradient' : 'fill'
  textAlign.value = handleElement.value?.text?.align || 'middle'
  await api.savePresentation()
}, { deep: true, immediate: true })

const { addHistorySnapshot } = useHistorySnapshot()

const updateElement = async(props: Partial<PPTShapeElement>) => {
  slidesStore.updateElement({ id: handleElementId.value, props })
  await api.savePresentation()
  addHistorySnapshot()
}

// Set the fill type: gradient, solid color
const updateFillType = (type: 'gradient' | 'fill') => {
  if (type === 'fill') {
    slidesStore.removeElementProps({ id: handleElementId.value, propName: 'gradient' })
    addHistorySnapshot()
  }
  else updateElement({ gradient: gradient.value })
}

// Set gradient fill
const updateGradient = (gradientProps: Partial<ShapeGradient>) => {
  if (!gradient.value) return
  const _gradient: ShapeGradient = { ...gradient.value, ...gradientProps }
  updateElement({ gradient: _gradient })
}

// set fill color
const updateFill = (value: string) => {
  updateElement({ fill: value })
}

// modify shape
const changeShape = (shape: ShapePoolItem) => {
  const { width, height } = handleElement.value as PPTShapeElement
  const props: Partial<PPTShapeElement> = {
    viewBox: shape.viewBox,
    path: shape.path,
    special: shape.special,
  }
  if (shape.pathFormula) {
    props.pathFormula = shape.pathFormula
    props.viewBox = [width, height]

    const pathFormula = SHAPE_PATH_FORMULAS[shape.pathFormula]
    if ('editable' in pathFormula) {
      props.path = pathFormula.formula(width, height, pathFormula.defaultValue)
      props.keypoint = pathFormula.defaultValue
    }
    else props.path = pathFormula.formula(width, height)
  }
  else {
    props.pathFormula = undefined
    props.keypoint = undefined
  }
  updateElement(props)
}
</script>

<style lang="scss" scoped>
.shape-style-panel {
  user-select: none;
}
.row {
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.font-select {
  max-width: 50%;
}
.font-size-btn {
  padding: 0;
}
.slider {
  flex: 3;
}
.title {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.shape-pool {
  width: auto;
  height: 190px;
  overflow: auto;
  padding: 5px;
  padding-right: 10px;
  border: 1px solid $borderColor;
  margin-bottom: 20px;
}
.shape-list {
  @include flex-grid-layout();
  flex-wrap: wrap;
}
.shape-item {
  @include flex-grid-layout-children(6, 14%);

  height: 0;
  padding-bottom: 14%;
  flex-shrink: 0;
}

.group {
  margin-top: 10px;
  display: flex;
  align-items: center;
  column-gap: 10px;
}
</style>
